/**
 * Time control: pick any hour of any day, or snap back to now.
 *
 * The planning case matters as much as the live one. "Where should I be at 21:00 on
 * Friday" is a question you answer on the sofa on Thursday, and it is the question the
 * whole model is best at.
 */

import { HEBREW_DAY_NAMES, type WeekMoment } from '@tlv/shared';

interface Props {
  moment: WeekMoment;
  onChange: (moment: WeekMoment) => void;
  isLive: boolean;
  onGoLive: () => void;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** 30-minute steps, so the slider resolution matches the opening-hours bitmap. */
const STEP_MINUTES = 30;

export function TimeSlider({ moment, onChange, isLive, onGoLive }: Props): JSX.Element {
  const hour = Math.floor(moment.minutesOfDay / 60);
  const minute = moment.minutesOfDay % 60;
  const timeLabel = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

  return (
    <div className="time-control">
      <div className="time-control-head">
        <div className="time-readout">
          <span className="time-readout-day">{DAY_LABELS[moment.day]}</span>
          <span className="time-readout-day-he" lang="he" dir="rtl">
            {HEBREW_DAY_NAMES[moment.day]}
          </span>
          <span className="time-readout-clock">{timeLabel}</span>
        </div>
        <button
          type="button"
          className={isLive ? 'now-button now-button-active' : 'now-button'}
          onClick={onGoLive}
          aria-pressed={isLive}
        >
          {isLive ? 'Live' : 'Now'}
        </button>
      </div>

      <div className="day-picker" role="group" aria-label="Day of week">
        {DAY_LABELS.map((label, day) => (
          <button
            key={label}
            type="button"
            className={day === moment.day ? 'day-chip day-chip-active' : 'day-chip'}
            onClick={() => onChange({ ...moment, day })}
            aria-pressed={day === moment.day}
          >
            {label}
          </button>
        ))}
      </div>

      <input
        className="hour-slider"
        type="range"
        min={0}
        max={24 * 60 - STEP_MINUTES}
        step={STEP_MINUTES}
        value={moment.minutesOfDay}
        onChange={(event) => onChange({ ...moment, minutesOfDay: Number(event.target.value) })}
        aria-label="Time of day"
        aria-valuetext={`${DAY_LABELS[moment.day]} ${timeLabel}`}
      />

      <div className="hour-scale" aria-hidden="true">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
    </div>
  );
}
